import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError, timer, of } from 'rxjs';
import { catchError, filter, finalize, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { AuthenticationService } from '../authentication.service';
import { authModel, storageApiKey } from '../models/auth';
import { FreezeOverlayService } from './freeze-overlay.service';

type QueueItem = {
    request: HttpRequest<any>;
    handler: HttpHandler;
    attempts: number;
    subject: Subject<HttpEvent<any>>;
};

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private isTokenRequesting = false;
    private tokenSubject = new BehaviorSubject<authModel | null>(null);

    private queue: QueueItem[] = [];
    private draining = false;
    private infoSnackRef: MatSnackBarRef<any> | null = null;

    private didLogout = false;
    private ipMismatchStrikes = 0;
    private exhaustedTransientCount = 0;

    private readonly MAX_ATTEMPTS = 4;
    private readonly BASE_DELAY_MS = 1000;
    private readonly BACKOFF = 1.9;
    private readonly JITTER = 0.3;
    private readonly MAX_DELAY_CAP = 15000;
    private readonly MAX_IP_MISMATCH_STRIKES = 3;
    private readonly MAX_TRANSIENT_EXHAUSTED = 12;
    private readonly MAX_QUEUE = 150;

    private readonly BYPASS_RE = /\/oauth\/|\/authentication\/|ipify|vision\.googleapis\.com|s3\.eu-west-1\.amazonaws\.com/i;

    private readonly FORCE_LOGOUT_TITLES = new Set([
        'invalid_ipaddress',
        'ip_not_allowed',
        'ip_forbidden',
        'invalid_client',
        'invalid_grant',
        'invalid_token',
        'token_revoked'
    ]);

    constructor(
        public authenticationService: AuthenticationService,
        private snackBar: MatSnackBar,
        private freeze: FreezeOverlayService,
        private zone: NgZone
    ) {
        if (typeof window !== 'undefined') {
            window.addEventListener('online', () => {
                this.zone.run(() => {
                    this.freeze.hide();
                    this.showRecoveredSnack();
                    this.exhaustedTransientCount = 0;
                    this.drainQueueIfNeeded();
                });
            });
            window.addEventListener('offline', () => {
                this.zone.run(() => this.freeze.show());
            });
        }
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.BYPASS_RE.test(request.url)) {
            return next.handle(request).pipe(
                catchError((err: HttpErrorResponse) => {
                    if (this.isNetworkDown(err)) {
                        this.freeze.show();
                        this.showOfflineInfoSnack();
                    } else if (err.status === 500) {
                        this.showServer500Toast();
                    } else if (this.shouldForceLogout(err)) {
                        this.logoutWithMessage(this.logoutMessageFor(err));
                    }
                    return throwError(() => err);
                })
            );
        }

        const currentUser = this.safeGetUser();
        if (!currentUser) {
            this.logoutWithMessage('Je sessie is verlopen. Log opnieuw in.');
            return throwError(() => new HttpErrorResponse({ status: 401 }));
        }

        if (!this.authenticationService.checkAccessTokenValidity()) {
            return this.handleUnauthorized(request, next);
        }

        const authedReq = request.clone({ setHeaders: { Authorization: `Bearer ${currentUser.access_token}` } });

        return next.handle(authedReq).pipe(
            catchError((err: HttpErrorResponse) => {
                if (this.isNetworkDown(err)) return this.queueAndBridge(request, next);

                if (this.shouldForceLogout(err)) {
                    this.logoutWithMessage(this.logoutMessageFor(err));
                    return throwError(() => err);
                }

                if (this.isRecoverableIpMismatch(err)) {
                    this.ipMismatchStrikes++;
                    if (this.ipMismatchStrikes >= this.MAX_IP_MISMATCH_STRIKES) {
                        this.logoutWithMessage('Je sessie is ongeldig vanwege IP-beperking. Log opnieuw in.');
                        return throwError(() => err);
                    }

                    if (this.isRefreshing || this.isTokenRequesting) {
                        return this.tokenSubject.pipe(
                            filter(m => m !== null),
                            take(1),
                            switchMap(m => next.handle(this.addTokenToRequest(request, m!.access_token)))
                        );
                    }

                    const apiKey = this.safeGetApiKey();
                    if (!apiKey || !this.authenticationService.checkApiKeyValidity()) {
                        return this.handleUnauthorized(request, next);
                    }

                    const snap = this.safeGetUser();
                    const username = (snap as any)?.username ?? '';
                    const deviceToken = (snap as any)?.device_token ?? '';

                    if (!username || !deviceToken) {
                        this.showActionSnack('Sessieherstel mislukt: username/device_token ontbreekt. Log opnieuw in.');
                        return this.handleUnauthorized(request, next);
                    }

                    this.isRefreshing = true;
                    this.isTokenRequesting = true;
                    this.tokenSubject.next(null);

                    return this.loginViaApiKeyAndRetry(apiKey.api_key, username, deviceToken, request, next).pipe(
                        finalize(() => (this.isTokenRequesting = false)),
                        shareReplay(1)
                    );
                }

                if (err.status === 500) {
                    this.showServer500Toast();
                    return throwError(() => err);
                }

                if (this.isTransient(err)) {
                    return this.queueAndBridge(request, next);
                }

                if (err.status === 403) {
                    this.showActionSnack('Er is een fout opgetreden');
                    return throwError(() => err);
                }

                if (!this.authenticationService.checkAccessTokenValidity() && this.authenticationService.checkRefreshTokenValidity()) {
                    return this.refreshTokenAndRetry(this.safeGetUser()!.refresh_token, request, next);
                }

                return this.handleUnauthorized(request, next);
            })
        );
    }

    private handleUnauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isRefreshing || this.isTokenRequesting) {
            return this.tokenSubject.pipe(
                filter(r => r !== null),
                take(1),
                switchMap(m => next.handle(this.addTokenToRequest(request, m!.access_token)))
            );
        }

        const currentUser = this.safeGetUser();

        if (currentUser) {
            if (this.authenticationService.checkRefreshTokenValidity()) {
                this.isRefreshing = true;
                return this.refreshTokenAndRetry(currentUser.refresh_token, request, next);
            } else if (this.authenticationService.checkApiKeyValidity()) {
                const apiKey = this.safeGetApiKey();
                const snap = this.safeGetUser();
                if (apiKey && snap) {
                    const username = (snap as any)?.username ?? '';
                    const deviceToken = (snap as any)?.device_token ?? '';
                    if (!username || !deviceToken) {
                        this.showActionSnack('Sessieherstel mislukt: username/device_token ontbreekt. Log opnieuw in.');
                        return throwError(() => new HttpErrorResponse({ status: 401 }));
                    }
                    this.isRefreshing = true;
                    this.isTokenRequesting = true;
                    return this.loginViaApiKeyAndRetry(apiKey.api_key, username, deviceToken, request, next).pipe(
                        finalize(() => (this.isTokenRequesting = false)),
                        shareReplay(1)
                    );
                }
            }
        }

        this.logoutWithMessage('Je sessie is verlopen. Log opnieuw in.');
        return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    private refreshTokenAndRetry(refreshToken: string, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.authenticationService.loginWithRefreshToken(refreshToken).pipe(
            switchMap((normalizedAuth: authModel) => {
                const bearer = normalizedAuth?.access_token ?? '';
                if (!bearer) return throwError(() => new HttpErrorResponse({ status: 401 }));

                this.ipMismatchStrikes = 0;
                this.tokenSubject.next(normalizedAuth);

                const deviceToken = (normalizedAuth as any)?.device_token ?? (this.safeGetUser() as any)?.device_token ?? '';
                if (deviceToken) {
                    this.authenticationService.requestApiKey(deviceToken).pipe(catchError(() => of(null))).subscribe(() => { });
                }

                return next.handle(this.addTokenToRequest(request, bearer));
            }),
            catchError((error: HttpErrorResponse) => this.handlePostAuthError(error, request, next)),
            finalize(() => (this.isRefreshing = false)),
            shareReplay(1)
        );
    }

    private loginViaApiKeyAndRetry(
        apiKey: string,
        username: string,
        deviceToken: string,
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return this.authenticationService.loginWithApiKey(apiKey, username, deviceToken).pipe(
            switchMap(() => {
                const stored = this.safeGetUser();
                const bearer = stored?.access_token ?? '';
                if (!bearer) {
                    this.showActionSnack('Nieuw toegangstoken ontbreekt.');
                    return throwError(() => new HttpErrorResponse({ status: 401 }));
                }

                this.ipMismatchStrikes = 0;
                this.tokenSubject.next(stored!);

                const retried = this.addTokenToRequest(request, bearer);

                return this.authenticationService.requestApiKey(deviceToken).pipe(
                    tap((k: storageApiKey) => { if (k?.api_key) this.safeSaveApiKey(k); }),
                    catchError(() => of(null)),
                    switchMap(() => next.handle(retried))
                );
            }),
            catchError((e: HttpErrorResponse) => this.handlePostAuthError(e, request, next)),
            finalize(() => (this.isRefreshing = false))
        );
    }

    private handlePostAuthError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (error.status === 400 && this.getProblemTitle(error) === 'invalid_request') {
            this.showActionSnack('Sessieherstel mislukt: ongeldig verzoek.');
            return throwError(() => error);
        }

        if (this.shouldForceLogout(error)) {
            this.logoutWithMessage(this.logoutMessageFor(error));
            return throwError(() => error);
        }

        if (this.isNetworkDown(error)) return this.queueAndBridge(request, next);
        if (error.status === 500) { this.showServer500Toast(); return throwError(() => error); }
        if (this.isTransient(error)) return this.queueAndBridge(request, next);

        return throwError(() => error);
    }

    private getProblemTitle(err: HttpErrorResponse): string {
        if (!err) return '';
        try {
            const e: any = err.error ?? {};
            const raw = (e.title ?? e.Title ?? e.error ?? e.error_description ?? '').toString();
            return raw.trim().toLowerCase();
        } catch { return ''; }
    }

    private isRecoverableIpMismatch(err: HttpErrorResponse): boolean {
        return err.status === 401 && this.getProblemTitle(err) === 'invalid_token_ip';
    }

    private shouldForceLogout(err: HttpErrorResponse): boolean {
        const title = this.getProblemTitle(err);
        return !!title && this.FORCE_LOGOUT_TITLES.has(title);
    }

    private logoutMessageFor(err: HttpErrorResponse): string {
        const title = this.getProblemTitle(err);
        switch (title) {
            case 'invalid_ipaddress':
            case 'ip_not_allowed':
            case 'ip_forbidden':
                return 'Je IP-adres is niet toegestaan. Log opnieuw in.';
            default:
                return 'Je sessie is ongeldig. Log opnieuw in.';
        }
    }

    private isTransient(err: HttpErrorResponse): boolean {
        if (err.status === 0) return true;
        if (err.status === 500) return false;
        if (err.status >= 501) return true;
        if (err.status === 429) return true;
        return false;
    }

    private isNetworkDown(err: HttpErrorResponse): boolean {
        return err.status === 0 && (err.error instanceof ProgressEvent || !navigator.onLine);
    }

    private addTokenToRequest(request: HttpRequest<any>, token: string | null) {
        return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    private showActionSnack(message: string): void {
        this.snackBar.open(message, undefined, { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom' });
    }

    private showServer500Toast(): void {
        this.snackBar.open('Actie mislukt door een serverfout.', undefined, { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom' });
    }

    private showOfflineInfoSnack(): void {
        if (this.infoSnackRef) return;
        this.infoSnackRef = this.snackBar.open('Geen verbinding met de server. Je acties worden gepauzeerd tot de verbinding terug is.', undefined, { duration: undefined, horizontalPosition: 'center', verticalPosition: 'bottom' });
    }

    private showRecoveredSnack(): void {
        if (this.infoSnackRef) { this.infoSnackRef.dismiss(); this.infoSnackRef = null; }
        this.snackBar.open('Verbonden! Acties worden hervat…', undefined, { duration: 2500, horizontalPosition: 'center', verticalPosition: 'bottom' });
    }

    private enqueue(item: QueueItem): void {
        if (this.queue.length >= this.MAX_QUEUE) this.queue.shift();
        this.queue.push(item);
    }

    private drainQueueIfNeeded(): void {
        if (this.draining) return;
        if (this.queue.length === 0) return;
        if (typeof navigator !== 'undefined' && !navigator.onLine) return;

        this.draining = true;

        const runRound = () => {
            if (this.queue.length === 0) { this.draining = false; return; }

            const batch = [...this.queue];
            this.queue = [];
            let pending = batch.length;

            batch.forEach(item => {
                const delay = this.calcDelay(item.attempts);
                timer(delay).pipe(take(1)).subscribe(() => {
                    const freshUser = this.safeGetUser();
                    const token = freshUser?.access_token ?? null;
                    const retryReq = token ? this.addTokenToRequest(item.request, token) : item.request;

                    item.handler.handle(retryReq).subscribe({
                        next: ev => { this.exhaustedTransientCount = 0; item.subject.next(ev); },
                        error: (e: HttpErrorResponse) => {
                            const offlineNow = this.isNetworkDown(e);
                            const transient = this.isTransient(e) || offlineNow;

                            if (transient && item.attempts + 1 < this.MAX_ATTEMPTS) {
                                this.queue.push({ ...item, attempts: item.attempts + 1 });
                            } else {
                                if (!offlineNow && transient) {
                                    this.exhaustedTransientCount++;
                                    if (this.exhaustedTransientCount >= this.MAX_TRANSIENT_EXHAUSTED) {
                                        this.logoutWithMessage('De server blijft onbereikbaar. Log opnieuw in.');
                                        item.subject.error(e);
                                        return;
                                    }
                                }
                                item.subject.error(e);
                            }
                        },
                        complete: () => item.subject.complete()
                    }).add(() => { if (--pending === 0) runRound(); });
                });
            });
        };

        runRound();
    }

    private calcDelay(attempt: number): number {
        const base = Math.min(this.BASE_DELAY_MS * Math.pow(this.BACKOFF, attempt), this.MAX_DELAY_CAP);
        const jitter = base * this.JITTER * (Math.random() * 2 - 1);
        return Math.max(300, Math.round(base + jitter));
    }

    private queueAndBridge(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const subject = new Subject<HttpEvent<any>>();
        this.enqueue({ request, handler: next, attempts: 0, subject });
        this.freeze.show();
        this.showOfflineInfoSnack();
        this.drainQueueIfNeeded();
        return subject.asObservable();
    }

    private safeGetUser(): authModel | null {
        try { return JSON.parse(localStorage.getItem('auth') || 'null'); } catch { return null; }
    }

    private safeGetApiKey(): storageApiKey | null {
        try { return JSON.parse(localStorage.getItem('apiKey') || 'null'); } catch { return null; }
    }

    private safeSaveApiKey(k: storageApiKey) {
        try { localStorage.setItem('apiKey', JSON.stringify(k)); } catch { }
    }

    private logoutWithMessage(message: string) {
        if (this.didLogout) return;
        this.didLogout = true;

        const err = new HttpErrorResponse({ status: 401, statusText: 'Logged out' });
        try { for (const item of this.queue) item.subject.error(err); } catch { }
        this.queue = [];
        this.draining = false;

        this.freeze.hide();
        if (this.infoSnackRef) { this.infoSnackRef.dismiss(); this.infoSnackRef = null; }
        this.authenticationService.logout();

        this.snackBar.open(message, undefined, { duration: 5000, horizontalPosition: 'center', verticalPosition: 'bottom' });
    }
}
